export type ProbillEntryStatus = "validating" | "valid" | "invalid";

export type DocDownloadStatus = "idle" | "queued" | "downloading" | "success" | "error";

export interface ProbillEntry {
  id: string;
  raw: string;
  value?: number;
  status: ProbillEntryStatus;
  errorMessage?: string;
  deliveryManifestNumber?: number;
}

export interface ProbillDocSelection {
  driverReceipt: boolean;
  shippingLabel: boolean;
  deliveryManifest: boolean;
}

export type ProbillSelectionMap = Record<number, ProbillDocSelection>;

export interface DocTypeDownloadStatusMap {
  shippingLabels: DocDownloadStatus;
  driverReceipts: DocDownloadStatus;
  deliveryManifests: DocDownloadStatus;
}

export const EMPTY_DOC_DOWNLOAD_STATUS: DocTypeDownloadStatusMap = {
  shippingLabels: "idle",
  driverReceipts: "idle",
  deliveryManifests: "idle",
};

export const DEFAULT_DOC_SELECTION: ProbillDocSelection = {
  driverReceipt: false,
  shippingLabel: false,
  deliveryManifest: false,
};

export const MAX_VALID_PROBILLS = 100;
export const MAX_MANIFEST_BATCH = 50;
export const PROBILL_REGEX = /^\d{6,8}$/;

export const formatProbillDisplay = (value: number): string => `PRO-${value}`;

export const formatManifestDisplay = (value: number): string => `MF-${value}`;

export const normalizeProbillToken = (token: string): string => {
  return token.trim().replace(/^pro-/i, "").replace(/\s+/g, "");
};

/** Format-only parse. API existence check happens separately. */
export const parseProbillToken = (
  token: string
): { raw: string; value?: number; formatValid: boolean } => {
  const trimmed = token.trim();
  if (!trimmed) {
    return { raw: trimmed, formatValid: false };
  }
  const normalized = normalizeProbillToken(trimmed);
  if (PROBILL_REGEX.test(normalized)) {
    return { raw: trimmed, value: Number(normalized), formatValid: true };
  }
  return { raw: trimmed, formatValid: false };
};

export const createEntryId = (): string =>
  `entry-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

export const extractDeliveryManifestNumber = (data: any): number | undefined => {
  const nested = data?.manifest?.delivery;
  if (typeof nested === "number" && nested > 0) return nested;
  const topLevel = data?.deliveryManifest;
  if (typeof topLevel === "number" && topLevel > 0) return topLevel;
  const history = data?.deliveryManifestNumber;
  if (typeof history === "number" && history > 0) return history;
  return undefined;
};
